import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { apiBasisUrl } from '../App';
import { KassenApi, type Bonierung } from '../api';
import { AnmeldeWaechter } from '../geraeteanmeldung';
import { quittiere } from '../kamera-scan';

const ZIEL_SCHLUESSEL = 'bilanx.kuechenmonitor.ziel';

function minutenSeit(iso: string, jetzt: number): number {
  return Math.max(0, Math.floor((jetzt - new Date(iso).getTime()) / 60_000));
}

function uhrzeit(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-AT', {
    timeZone: 'Europe/Vienna',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function menge(m: number): string {
  return String(Math.abs(m)).replace('.', ',');
}

/**
 * Küchenmonitor (`?kueche=1`): offene Bestellungen einer Produktionsstelle als
 * Karten, älteste zuerst; Tippen auf „Fertig“ erledigt sie. Läuft auf einem
 * Tablet oder Bildschirm in Küche oder Schank, das wie eine Kasse verbunden ist
 * (Kassen-ID und Kassenschlüssel aus der Einrichtung). Aktualisiert alle 5 s.
 */
export function Kuechenmonitor() {
  const kassenId = localStorage.getItem('bilanx.kassenId') ?? '';
  const api = useMemo(
    () =>
      new KassenApi({
        basisUrl: apiBasisUrl(),
        token: () => localStorage.getItem('bilanx.kassenschluessel'),
        // Ungültiger Schlüssel: nicht alle 5 s weiter anfragen (Fehlversuche, Adresssperre).
        anmeldung: new AnmeldeWaechter(),
      }),
    [],
  );
  const [ziel, setZiel] = useState<string | null>(() => localStorage.getItem(ZIEL_SCHLUESSEL));
  const [ziele, setZiele] = useState<string[]>([]);
  const [offen, setOffen] = useState<Bonierung[]>([]);
  const [erledigt, setErledigt] = useState<Bonierung[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [jetzt, setJetzt] = useState(() => Date.now());
  const bekannt = useRef<Set<number> | null>(null);

  const laden = useCallback(async () => {
    if (kassenId === '') {
      return;
    }
    try {
      const r = await api.bonierungen(kassenId, ziel);
      setZiele(r.ziele);
      setOffen(r.offen);
      setErledigt(r.erledigt);
      setFehler(null);
      // Neue Bestellung: kurzer Ton (nicht beim ersten Laden)
      const ids = new Set(r.offen.map((b) => b.id));
      if (bekannt.current !== null && [...ids].some((id) => !bekannt.current?.has(id))) {
        quittiere();
      }
      bekannt.current = ids;
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }, [api, kassenId, ziel]);

  useEffect(() => {
    bekannt.current = null;
    void laden();
    const t = setInterval(() => {
      void laden();
      setJetzt(Date.now());
    }, 5_000);
    return () => {
      clearInterval(t);
    };
  }, [laden]);

  async function setze(b: Bonierung, fertig: boolean): Promise<void> {
    try {
      await api.bonierungErledigt(kassenId, b.id, fertig);
      await laden();
    } catch (e: unknown) {
      setFehler(e instanceof Error ? e.message : String(e));
    }
  }

  if (kassenId === '') {
    return (
      <main className="kuechenmonitor">
        <p className="fehler">
          Dieses Gerät ist noch nicht verbunden. Zuerst die Kasse öffnen und unter „Einrichtung“
          Kassen-ID und Kassenschlüssel eintragen.
        </p>
      </main>
    );
  }

  return (
    <main className="kuechenmonitor">
      <header>
        <h1>{ziel ?? 'Alle Stellen'}</h1>
        <div className="chips" role="group" aria-label="Produktionsstelle">
          <button
            type="button"
            className={ziel === null ? 'aktiv' : ''}
            onClick={() => {
              localStorage.removeItem(ZIEL_SCHLUESSEL);
              setZiel(null);
            }}
          >
            Alle
          </button>
          {ziele.map((z) => (
            <button
              key={z}
              type="button"
              className={ziel === z ? 'aktiv' : ''}
              onClick={() => {
                localStorage.setItem(ZIEL_SCHLUESSEL, z);
                setZiel(z);
              }}
            >
              {z}
            </button>
          ))}
        </div>
        <span className="hinweis">{offen.length} offen</span>
      </header>
      {fehler !== null && <p className="fehler">{fehler}</p>}
      {offen.length === 0 && <p className="leer">Keine offenen Bestellungen.</p>}
      <div className="bestellungen">
        {offen.map((b) => {
          const min = minutenSeit(b.erstelltAm, jetzt);
          return (
            <article
              key={b.id}
              className={`bestellung${min >= 20 ? ' spaet' : min >= 10 ? ' wartet' : ''}`}
            >
              <header>
                <strong>{b.tisch ?? 'Direktverkauf'}</strong>
                <span>
                  {uhrzeit(b.erstelltAm)} · {min} min
                </span>
              </header>
              {ziel === null && <small className="hinweis">{b.ziel}</small>}
              <ul>
                {b.zeilen.map((z, i) => (
                  <li key={i} className={z.menge < 0 ? 'storno' : ''}>
                    {z.menge < 0 ? 'STORNO ' : ''}
                    <b>{menge(z.menge)} ×</b> {z.bezeichnung}
                  </li>
                ))}
              </ul>
              {b.notiz !== null && <p className="notiz">{b.notiz}</p>}
              {b.bediener !== null && <small className="hinweis">{b.bediener}</small>}
              <button type="button" className="taste" onClick={() => void setze(b, true)}>
                Fertig
              </button>
            </article>
          );
        })}
      </div>
      {erledigt.length > 0 && (
        <footer>
          <span className="hinweis">Zuletzt fertig:</span>
          {erledigt.map((b) => (
            <button
              key={b.id}
              type="button"
              className="taste zweit"
              title="Zurück in die offenen Bestellungen"
              onClick={() => void setze(b, false)}
            >
              {b.tisch ?? 'Direkt'} {uhrzeit(b.erstelltAm)} ↩
            </button>
          ))}
        </footer>
      )}
    </main>
  );
}
